import { useCallback } from 'react';
import Button from 'v2/components/Button';
import CertificationStatusPill from 'v2/components/CertificationStatusPill';

import { getCertificationStatus } from '@@utils/certifications';

import HorizontalContainer from 'components/containers/HorizontalContainer';

import useTranslation from './hooks/useTranslation';

import {
    ADD_CERTIFICATION_TEST_ID,
    HIDDEN_COUNT_PREFIX,
    NO_HIDDEN_CERTIFICATIONS,
    TEST_ID,
    getCertificationTooltip,
    getHiddenCertifications,
    getHiddenCertificationStates,
    getVisibleCertifications
} from './constants';
import type { FunctionComponent, Props, ReactMouseEvent } from './types';

import styles from './styles.module.scss';

const CertificationStatesCell: FunctionComponent<Props> = ({
    certifications,
    className = '',
    memberName,
    onAddCertification,
    testId = TEST_ID
}) => {
    const {
        ADD_CERTIFICATION,
        ADD_CERTIFICATION_LABEL,
        EXPIRED_TOOLTIP,
        EXPIRES_IN_DAY_TOOLTIP,
        EXPIRES_IN_DAYS_TOOLTIP,
        EXPIRES_TODAY_TOOLTIP,
        HIDDEN_CERTIFICATIONS_TOOLTIP,
        NO_EXPIRATION_TOOLTIP,
        VALID_THROUGH_TOOLTIP
    } = useTranslation();

    // The row itself opens the member, so the link has to keep its own click.
    const handleAddClick = useCallback(
        (event: ReactMouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onAddCertification();
        },
        [onAddCertification]
    );

    const hiddenCertificationCount = getHiddenCertifications(certifications).length;

    const hiddenCertificationsTooltip = HIDDEN_CERTIFICATIONS_TOOLTIP(
        getHiddenCertificationStates(certifications)
    );

    const tooltipTranslations = {
        expired: EXPIRED_TOOLTIP,
        expiresInDay: EXPIRES_IN_DAY_TOOLTIP,
        expiresInDays: EXPIRES_IN_DAYS_TOOLTIP,
        expiresToday: EXPIRES_TODAY_TOOLTIP,
        noExpiration: NO_EXPIRATION_TOOLTIP,
        validThrough: VALID_THROUGH_TOOLTIP
    };

    if (!certifications?.length) {
        return (
            <HorizontalContainer
                className={`${className} ${styles.certificationStatesCell}`}
                testId={testId}
            >
                <Button
                    ariaLabel={ADD_CERTIFICATION_LABEL(memberName)}
                    className={styles.addCertification}
                    onClick={handleAddClick}
                    testId={ADD_CERTIFICATION_TEST_ID}
                >
                    {ADD_CERTIFICATION}
                </Button>
            </HorizontalContainer>
        );
    }

    return (
        <HorizontalContainer
            className={`${className} ${styles.certificationStatesCell}`}
            testId={testId}
        >
            {getVisibleCertifications(certifications).map(certification => (
                <CertificationStatusPill
                    key={certification.id}
                    status={getCertificationStatus(certification.certificationExpirationDate)}
                    text={certification.stateOfCertification}
                    title={getCertificationTooltip(certification, tooltipTranslations)}
                />
            ))}

            {hiddenCertificationCount > NO_HIDDEN_CERTIFICATIONS ? (
                <CertificationStatusPill
                    text={`${HIDDEN_COUNT_PREFIX}${hiddenCertificationCount}`}
                    title={hiddenCertificationsTooltip}
                />
            ) : null}
        </HorizontalContainer>
    );
};

export default CertificationStatesCell;
